import { create } from 'zustand';
import { leaf, leafOrder } from '../components/terminal/paneTreeModel';
import { createPaneActions, newPaneId } from './terminalPaneActions';
import { recordTerminalPane } from '../services/telemetry/terminalUsage';

/**
 * Terminal extension state: the open tabs, the panes inside them, and which
 * tab is active. Session-only by design — shells cannot be restored across
 * an app restart, so nothing here persists (saved LAYOUTS live in the
 * terminal config file, not here).
 *
 * Tab:  { id, label, locked?, layout, focusedPaneId, zoomedPaneId, broadcast, mru }
 *   `layout` is a split tree (components/terminal/paneTreeModel.js);
 *   `locked` marks the AI tab — one pane, no split, no broadcast.
 * Pane: { id, tabId, kind: 'local' | 'ssh', label,
 *         shellKey?, connId?, ai?, termId?, profileId?, launch?, cwd?,
 *         liveCwd?, title?, status, exitCode, restartNonce }
 *   status: 'starting' | 'running' | 'exited' | 'error'
 *
 * The legacy actions (`openLocalTab`, `openSshTab`, `openAiTab`, `closeTab`,
 * `markExited`, `setActiveTab`) keep their names, arguments and return
 * values (a tab id) — `McpTerminalApprovalHost` and `graph/sshHostActions`
 * call them. Each creates a tab holding a single pane.
 */
let seq = 0;
const nextId = () => `term-tab-${Date.now().toString(36)}-${seq++}`;

const makePane = (tabId, spec) => ({
  status: 'starting',
  exitCode: null,
  restartNonce: 0,
  ...spec,
  id: spec.id || newPaneId(),
  tabId,
});

/** Build a tab with one pane; returns the state patch and the tab id. */
function withNewTab(s, label, spec, extra = {}) {
  const id = nextId();
  const pane = makePane(id, spec);
  const tab = {
    id,
    label,
    layout: leaf(pane.id),
    focusedPaneId: pane.id,
    zoomedPaneId: null,
    broadcast: false,
    mru: [pane.id],
    ...extra,
  };
  return {
    id,
    patch: {
      tabs: [...s.tabs, tab],
      panes: { ...s.panes, [pane.id]: pane },
      activeTabId: id,
    },
  };
}

/** Every pane of a tab, in reading order. */
export const tabPanes = (state, tab) =>
  tab ? leafOrder(tab.layout).map((id) => state.panes[id]).filter(Boolean) : [];

/** A tab's "kind" for icons: that of its first pane. */
export const tabKind = (state, tab) => tabPanes(state, tab)[0]?.kind || 'local';

export const useTerminalExtStore = create((set, get) => ({
  tabs: [],
  panes: {},
  activeTabId: null,

  // `tabExtra`: fields kept on the TAB, e.g. `returnTo` ({ panel, label }) for
  // a tab opened from another page ("Open in terminal" on a project).
  openLocalTab: (label, shellKey, extraSpec = {}, tabExtra = {}) => {
    let id = null;
    set((s) => {
      const r = withNewTab(s, label || 'Terminal', { kind: 'local', label: label || 'Terminal', shellKey, ...extraSpec }, tabExtra);
      id = r.id;
      return r.patch;
    });
    recordTerminalPane({ kind: 'local', shellKey, ...extraSpec });
    return id;
  },

  /**
   * The tab an approved MCP command runs in — one per connection, and never
   * the user's own tab for that connection.
   *
   * Two things make it unlike `openSshTab`. It is bound to a session **Rust has
   * already opened** (`termId`), because the command is sent from a worker
   * thread the instant the user approves and the webview cannot be first; and
   * it carries `ai`, which the view badges and the hook uses to refuse
   * arbitrary keystrokes. Sharing the user's tab instead would mean typing a
   * command into whatever they had running there. It is `locked`: never
   * split, never a broadcast target.
   */
  openAiTab: (connId, termId, label) => {
    const s = get();
    const existing = Object.values(s.panes).find((p) => p.kind === 'ssh' && p.ai && p.connId === connId);
    if (existing) {
      set((st) => ({
        // A reconnect gives the same connection a NEW session id. The pane has
        // to follow it, or it sits listening to a terminal that no longer
        // exists and shows nothing while commands run.
        panes: { ...st.panes, [existing.id]: { ...existing, termId, status: 'starting', exitCode: null } },
        activeTabId: existing.tabId,
      }));
      return existing.tabId;
    }
    let id = null;
    set((st) => {
      const r = withNewTab(
        st,
        label || 'AI',
        { kind: 'ssh', ai: true, label: label || 'AI', connId, termId },
        { locked: true }
      );
      id = r.id;
      return r.patch;
    });
    recordTerminalPane({ kind: 'ssh', ai: true });
    return id;
  },

  /**
   * Opening a connection again focuses the pane that already hosts it (in
   * whichever tab or split) instead of opening a second session. A user can
   * still put the same connection in a split explicitly.
   */
  openSshTab: (connId, label) => {
    const s = get();
    const existing = Object.values(s.panes).find((p) => p.kind === 'ssh' && !p.ai && p.connId === connId);
    if (existing) {
      get().focusPane(existing.id);
      set({ activeTabId: existing.tabId });
      return existing.tabId;
    }
    let id = null;
    set((st) => {
      const r = withNewTab(st, label || 'SSH', { kind: 'ssh', label: label || 'SSH', connId });
      id = r.id;
      return r.patch;
    });
    recordTerminalPane({ kind: 'ssh' });
    return id;
  },

  closeTab: (id) =>
    set((s) => {
      const tab = s.tabs.find((t) => t.id === id);
      const remaining = s.tabs.filter((t) => t.id !== id);
      let nextActive = s.activeTabId;
      if (nextActive === id) {
        const idx = s.tabs.findIndex((t) => t.id === id);
        nextActive = remaining[Math.min(idx, remaining.length - 1)]?.id ?? null;
      }
      const panes = { ...s.panes };
      if (tab) leafOrder(tab.layout).forEach((pid) => delete panes[pid]);
      return { tabs: remaining, panes, activeTabId: nextActive };
    }),

  /** Legacy: accepts a tab id (marks its focused pane) or a pane id. */
  markExited: (id, code = null) => {
    const s = get();
    const tab = s.tabs.find((t) => t.id === id);
    const paneId = tab ? tab.focusedPaneId : id;
    get().setPaneStatus(paneId, 'exited', code);
  },

  setActiveTab: (id) => set({ activeTabId: id }),

  /**
   * A new tab laid out as `tree`, whose leaves are the ids of `specs` (a
   * saved layout being opened). Returns the tab id.
   */
  openTreeTab: (label, tree, specs) => {
    const id = nextId();
    const order = leafOrder(tree);
    set((s) => {
      const panes = { ...s.panes };
      specs.forEach((spec) => {
        panes[spec.id] = makePane(id, spec);
      });
      const tab = { id, label, layout: tree, focusedPaneId: order[0], zoomedPaneId: null, broadcast: false, mru: [order[0]] };
      return { tabs: [...s.tabs, tab], panes, activeTabId: id };
    });
    return id;
  },

  renameTab: (id, label) =>
    set((s) => ({ tabs: s.tabs.map((t) => (t.id === id ? { ...t, label } : t)) })),

  ...createPaneActions(set, get, makePane),
}));
